import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Activity,
  AlertTriangle,
  Braces,
  Check,
  CheckCircle2,
  ChevronDown,
  Clock3,
  Cloud,
  Copy,
  Database,
  Gauge,
  Loader2,
  RefreshCw,
  Server,
  ShieldCheck,
  WifiOff,
  XCircle,
} from "lucide-react";
import { checkHealth, HealthResponse, HealthServiceInfo, ServiceHealthState } from "./healthApi";
import styles from "./HealthPage.module.css";

interface ServiceCardItem {
  key: string;
  name: string;
  description: string;
  icon: React.ElementType;
  info: HealthServiceInfo;
}

const statusLabels: Record<ServiceHealthState, string> = {
  healthy: "Çalışıyor",
  degraded: "Kısıtlı",
  down: "Çalışmıyor",
  unknown: "Bilinmiyor",
};

const timeFormatter = new Intl.DateTimeFormat("tr-TR", {
  hour: "2-digit",
  minute: "2-digit",
  second: "2-digit",
});

function formatTime(value?: string) {
  if (!value) return "Henüz kontrol edilmedi";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : timeFormatter.format(date);
}

function statusIcon(status: ServiceHealthState) {
  if (status === "healthy") return <CheckCircle2 />;
  if (status === "degraded") return <AlertTriangle />;
  if (status === "down") return <XCircle />;
  return <WifiOff />;
}

export default function HealthPage() {
  const [health, setHealth] = useState<HealthResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState("");
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [rawOpen, setRawOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  const loadHealth = useCallback(async (initial = false) => {
    try {
      if (initial) setLoading(true);
      else setRefreshing(true);
      setError("");
      setHealth(await checkHealth());
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || "Health servisine bağlanılamadı.");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => { loadHealth(true); }, [loadHealth]);

  useEffect(() => {
    if (!autoRefresh) return undefined;
    const timer = window.setInterval(() => loadHealth(false), 30000);
    return () => window.clearInterval(timer);
  }, [autoRefresh, loadHealth]);

  const services = useMemo<ServiceCardItem[]>(() => health ? [
    { key: "api", name: "Drumly API", description: "HTTP uygulama servisi", icon: Server, info: health.api },
    { key: "mongodb", name: "MongoDB", description: "Katalog ve uygulama verileri", icon: Database, info: health.mongodb },
    { key: "firebase", name: "Firebase", description: "Kimlik ve push servisleri", icon: Cloud, info: health.firebase },
  ] : [], [health]);

  const healthyCount = services.filter((service) => service.info.status === "healthy").length;
  const overall = health?.overall || (error ? "down" : "unknown");
  const overallTitle = overall === "healthy"
    ? "Tüm sistemler operasyonel"
    : overall === "degraded"
      ? "Bazı servislerde sorun var"
      : overall === "down"
        ? "Health servisine ulaşılamıyor"
        : "Sistem durumu kontrol ediliyor";

  const copyRaw = async () => {
    if (!health) return;
    try {
      await navigator.clipboard.writeText(JSON.stringify(health.raw, null, 2));
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1800);
    } catch {
      setCopied(false);
    }
  };

  return (
    <main className={styles.page}>
      <section className={`${styles.hero} ${styles[overall]}`}>
        <div className={styles.heroGlow} />
        <div className={styles.heroContent}>
          <span className={styles.eyebrow}><Activity size={15} /> SİSTEM DURUMU</span>
          <div className={styles.heroStatus}>
            <span className={styles.statusOrb}>{loading ? <Loader2 className={styles.spin} /> : statusIcon(overall)}</span>
            <div>
              <h1>{overallTitle}</h1>
              <p>
                {overall === "healthy"
                  ? "Drumly altyapısı normal şekilde çalışıyor."
                  : overall === "degraded"
                    ? "Sistem erişilebilir, ancak en az bir bağımlılık kısıtlı durumda."
                    : error || "API ve bağlı servislerin durumu doğrulanıyor."}
              </p>
            </div>
          </div>
        </div>
        <div className={styles.heroActions}>
          <div className={styles.checkedAt}><span>Son kontrol</span><strong>{formatTime(health?.checkedAt)}</strong></div>
          <button className={styles.refreshButton} onClick={() => loadHealth(false)} disabled={loading || refreshing}>
            <RefreshCw size={17} className={refreshing ? styles.spin : ""} />
            {refreshing ? "Kontrol ediliyor…" : "Şimdi kontrol et"}
          </button>
        </div>
      </section>

      {error && (
        <div className={styles.errorBanner} role="alert">
          <WifiOff size={19} />
          <div><strong>Health endpoint’ine ulaşılamadı</strong><span>{error}</span></div>
          <button onClick={() => loadHealth(false)}>Tekrar dene</button>
        </div>
      )}

      <section className={styles.summary}>
        <article><span><ShieldCheck /></span><div><strong>{healthyCount}/{services.length || 3}</strong><small>Operasyonel servis</small></div></article>
        <article><span><Gauge /></span><div><strong>{health ? `${health.latencyMs} ms` : "—"}</strong><small>API yanıt süresi</small></div></article>
        <article><span><Clock3 /></span><div><strong>30 sn</strong><small>Otomatik kontrol aralığı</small></div></article>
        <article className={styles.autoCard}>
          <div><strong>Canlı izleme</strong><small>Health durumunu otomatik yenile</small></div>
          <button
            className={`${styles.switch} ${autoRefresh ? styles.switchOn : ""}`}
            onClick={() => setAutoRefresh((current) => !current)}
            role="switch"
            aria-checked={autoRefresh}
            aria-label="Otomatik yenileme"
          ><span /></button>
        </article>
      </section>

      <section className={styles.serviceSection}>
        <header>
          <div><span>BAĞIMLILIKLAR</span><h2>Servis kontrolleri</h2></div>
          {autoRefresh && <small className={styles.liveBadge}><i /> CANLI</small>}
        </header>

        {loading ? (
          <div className={styles.loading}><Loader2 className={styles.spin} /><strong>Servisler kontrol ediliyor</strong><span>Health endpoint’inden güncel durum alınıyor…</span></div>
        ) : (
          <div className={styles.serviceGrid}>
            {services.map((service) => {
              const Icon = service.icon;
              return (
                <article key={service.key} className={`${styles.serviceCard} ${styles[service.info.status]}`}>
                  <header><span className={styles.serviceIcon}><Icon /></span><span className={styles.serviceStatus}>{statusIcon(service.info.status)} {statusLabels[service.info.status]}</span></header>
                  <h3>{service.name}</h3>
                  <p>{service.description}</p>
                  <footer><span>Son kontrol</span><strong>{formatTime(health?.checkedAt)}</strong></footer>
                </article>
              );
            })}
          </div>
        )}
      </section>

      <section className={styles.diagnostics}>
        <button className={styles.diagnosticsHeader} onClick={() => setRawOpen((current) => !current)} aria-expanded={rawOpen}>
          <span className={styles.codeIcon}><Braces /></span>
          <span><strong>API yanıtı ve tanılama</strong><small>Health endpoint’inden dönen ham JSON verisi</small></span>
          <ChevronDown className={rawOpen ? styles.chevronOpen : ""} />
        </button>
        {rawOpen && (
          <div className={styles.rawPanel}>
            <header><span>GET /api/v1/health/</span><button onClick={copyRaw} disabled={!health}>{copied ? <Check /> : <Copy />}{copied ? "Kopyalandı" : "JSON’u kopyala"}</button></header>
            <pre>{health ? JSON.stringify(health.raw, null, 2) : JSON.stringify({ error: error || "Yanıt bekleniyor" }, null, 2)}</pre>
          </div>
        )}
      </section>
    </main>
  );
}
